import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import SalesByEmployeeReportPage from "./SalesByEmployeeReportPage";
import TipsByEmployeeReportPage from "./TipsByEmployeeReportPage";
import TipAllocationReportPage from "./TipAllocationReportPage";
import EmployeeHoursReportPage from "./EmployeeHoursReportPage";
import EmployeeHoursByJobRoleReportPage from "./EmployeeHoursByJobRoleReportPage";

const EMPLOYEES_TABS_OWNER = [
  { id: "revenue", label: "Revenue" },
  { id: "tipDistribution", label: "Tip Distribution" },
  { id: "labor", label: "Labor" },
];

const EMPLOYEES_TABS_MANAGER = [{ id: "revenue", label: "Revenue" }];

type RevenueView = "sales" | "tips";
type LaborView = "byEmployee" | "byRole";

export default function EmployeesReportPage() {
  const { hasRestaurant } = useAuth();
  const isOwnerAtStore = hasRestaurant?.storeStaffRole === "OWNER";
  const employeesTabs = isOwnerAtStore ? EMPLOYEES_TABS_OWNER : EMPLOYEES_TABS_MANAGER;
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [activeTab, setActiveTab] = useState("revenue");
  const [revenueView, setRevenueView] = useState<RevenueView>("sales");
  const [laborView, setLaborView] = useState<LaborView>("byEmployee");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const input = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );

  // When manager, only "revenue" is allowed; reset activeTab if they landed on owner-only tab (e.g. direct URL)
  useEffect(() => {
    if (!isOwnerAtStore && activeTab !== "revenue") {
      setActiveTab("revenue");
    }
  }, [isOwnerAtStore, activeTab]);

  const renderSubToggle = (
    options: { id: string; label: string }[],
    activeId: string,
    onSelect: (id: string) => void
  ) => (
    <div className="flex rounded-full bg-[#f3f4f6] p-1 w-fit mb-4">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onSelect(opt.id)}
          className={`py-2 px-4 text-sm font-medium rounded-full transition-all duration-150 ${
            activeId === opt.id
              ? "bg-white text-[#111827] shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
              : "bg-transparent text-[#6b7280] hover:text-[#374151]"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const tabContent = () => {
    switch (activeTab) {
      case "revenue":
        return (
          <>
            {isOwnerAtStore &&
              renderSubToggle(
                [
                  { id: "sales", label: "Sales" },
                  { id: "tips", label: "Direct Tips" },
                ],
                revenueView,
                (id) => setRevenueView(id as RevenueView)
              )}
            {!isOwnerAtStore && input && <SalesByEmployeeReportPage embedded input={input} />}
            {isOwnerAtStore && revenueView === "sales" && input && <SalesByEmployeeReportPage embedded input={input} />}
            {isOwnerAtStore && revenueView === "tips" && input && <TipsByEmployeeReportPage embedded input={input} />}
          </>
        );
      case "tipDistribution":
        return input ? <TipAllocationReportPage embedded input={input} /> : null;
      case "labor":
        return (
          <>
            {renderSubToggle(
              [
                { id: "byEmployee", label: "By Employee" },
                { id: "byRole", label: "By Role" },
              ],
              laborView,
              (id) => setLaborView(id as LaborView)
            )}
            {laborView === "byEmployee" && input && <EmployeeHoursReportPage embedded input={input} />}
            {laborView === "byRole" && input && <EmployeeHoursByJobRoleReportPage embedded input={input} />}
          </>
        );
      default:
        return input ? <SalesByEmployeeReportPage embedded input={input} /> : null;
    }
  };

  return (
    <ReportPageLayout
      hideTitle
      toolbar={
        <div className="flex items-center justify-between gap-4 w-full min-w-0">
          <ReportTabs
            tabs={employeesTabs}
            activeTabId={activeTab}
            onSelect={setActiveTab}
            inline
          />
          <span className="shrink-0">
            <ReportDateRangePicker
              value={datePreset}
              onChange={(preset) => setDatePreset(preset)}
            />
          </span>
        </div>
      }
    >
      {tabContent()}
    </ReportPageLayout>
  );
}
